/**
 * The push state machine, rendered as it is defined in 04-contracts.md §6.
 *
 * Two rules the UI must not break:
 *  1. Every state renders honestly — including `blocked`, `needs_fix`, `queued_for_quota`
 *     and `reversed`. None of them is collapsed into "error".
 *  2. Nothing here may imply a campaign is serving. `live_paused` is the terminal
 *     success state in v1, and it is labelled "Live — paused" with the unpause action
 *     attributed to a human in the platform UI.
 */
import type { PushState } from '@/api/types'
import {
  HUMAN_UNPAUSE_LOCATION,
  STATE_COPY,
  ladderFor,
  nextStates,
  type LadderStepStatus,
  type StateTone,
} from '@/lib/pushState'
import { Badge, Cap, type Tone } from './ui'

const STATUS_TONE: Record<LadderStepStatus, Tone> = {
  done: 'ok',
  current: 'forge',
  failed: 'danger',
  skipped: 'neutral',
  pending: 'neutral',
}

/** State-machine tones are named for meaning; Badge tones are named for colour. */
const TONE_MAP: Record<StateTone, Tone> = {
  neutral: 'neutral',
  running: 'forge',
  attention: 'warn',
  danger: 'danger',
  success: 'ok',
}

const STATUS_LABEL: Record<LadderStepStatus, string> = {
  done: 'done',
  current: 'in progress',
  failed: 'stopped here',
  skipped: 'skipped',
  pending: 'waiting',
}

export function PushStateLadder({ state }: { state: PushState }) {
  const steps = ladderFor(state)
  const copy = STATE_COPY[state]
  const next = nextStates(state)

  return (
    <div data-testid="push-state-ladder" data-state={state} className="flex flex-col gap-3">
      <ol className="flex flex-col gap-0" aria-label="Push progress">
        {steps.map((step, index) => {
          const stepCopy = STATE_COPY[step.state]
          const isCurrent = step.status === 'current'
          const isBranch = isCurrent && step.state !== state
          return (
            <li key={step.state} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  aria-hidden="true"
                  className={`mt-[6px] size-2.5 shrink-0 rounded-full border ${
                    step.status === 'done'
                      ? 'border-signal-ok bg-signal-ok'
                      : step.status === 'failed'
                        ? 'border-signal-danger bg-signal-danger'
                        : isCurrent
                          ? 'border-forge-400 bg-forge-400 pulse-dot'
                          : 'border-ink-600 bg-ink-900'
                  }`}
                />
                {index < steps.length - 1 ? <span className="my-0.5 w-px flex-1 bg-ink-700" /> : null}
              </div>
              <div className="min-w-0 pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`text-[13px] font-medium ${step.status === 'pending' ? 'text-mist-500' : 'text-mist-100'}`}
                  >
                    {stepCopy.label}
                  </span>
                  <Badge tone={STATUS_TONE[step.status]}>{STATUS_LABEL[step.status]}</Badge>
                  {isBranch ? <Badge tone="danger">interrupted</Badge> : null}
                </div>
                {step.status !== 'pending' ? (
                  <p className="mt-0.5 max-w-prose text-[12px] text-mist-400">{stepCopy.detail}</p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>

      <div
        data-testid="push-state-summary"
        className={`rounded-lg border px-3.5 py-3 ${
          copy.tone === 'danger'
            ? 'border-signal-danger/45 bg-signal-danger/10'
            : copy.tone === 'attention'
              ? 'border-signal-warn/45 bg-signal-warn/10'
              : copy.tone === 'success'
                ? 'border-signal-ok/45 bg-signal-ok/10'
                : 'border-ink-700 bg-ink-850'
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold text-mist-100">{copy.label}</span>
          {copy.terminal ? <Badge tone={TONE_MAP[copy.tone]}>terminal</Badge> : null}
          {copy.needsUser ? <Badge tone="warn">needs you</Badge> : null}
          <span className="num ml-auto text-[11px] text-mist-500">{state}</span>
        </div>
        <p className="mt-1 max-w-prose text-[12.5px] text-mist-300">{copy.detail}</p>
        {state === 'live_paused' ? (
          <p
            data-testid="paused-not-serving"
            className="mt-2 rounded-md border border-signal-ok/35 bg-ink-950/40 px-2.5 py-2 text-[12.5px] text-mist-200"
          >
            Nothing is serving. To start delivery you must unpause the campaigns yourself in{' '}
            {HUMAN_UNPAUSE_LOCATION} — AdForge never unpauses, and v1 never manages bids.
          </p>
        ) : null}
        {next.length > 0 ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Cap>Next</Cap>
            {next.map((candidate) => (
              <Badge key={candidate} tone="neutral">
                {STATE_COPY[candidate].label}
              </Badge>
            ))}
          </div>
        ) : (
          <div className="mt-2">
            <Cap>Next</Cap>
            <span className="ml-2 text-[12px] text-mist-400">
              nothing — this push is finished
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
